import React, { useRef, useState } from 'react';
import { useTexture, Html } from '@react-three/drei';
import { Vector3, Mesh, DoubleSide } from 'three';
import { useFrame } from '@react-three/fiber';
import { useSatelliteStore } from '../../store/satelliteStore';
import { HIGH_PERFORMANCE_THRESHOLD } from './SatelliteMarker';

// Scale and distance multipliers to make the view more manageable
const SCALE_MULTIPLIER = 0.3;

// Moon data
const MOON = {
  name: 'Moon',
  diameter: 3475, // km
  texture: '/textures/moon.jpg',
  rotationSpeed: 0.003,
  orbitSpeed: 0.015,
  orbitRadius: 2, // Scaled distance from Earth
  info: {
    type: 'Natural satellite',
    distance: '384,400 km from Earth',
    temperature: '-233°C to 123°C',
    day: '27.3 Earth days'
  }
};

// Planet data with adjusted positions for better visualization
const PLANETS = [
  {
    name: 'Mercury',
    diameter: 4879,
    texture: '/textures/mercury.jpg',
    rotationSpeed: 0.01,
    position: new Vector3(15, 3, -10),
    info: {
      type: 'Terrestrial planet',
      distance: '57.9 million km from Sun',
      temperature: '-180°C to 430°C',
      day: '176 Earth days'
    }
  },
  {
    name: 'Venus',
    diameter: 12104,
    texture: '/textures/venus.jpg',
    rotationSpeed: 0.0067,
    position: new Vector3(-20, 5, -15),
    info: {
      type: 'Terrestrial planet',
      distance: '108.2 million km from Sun',
      temperature: '462°C',
      day: '243 Earth days'
    }
  },
  {
    name: 'Mars',
    diameter: 6792,
    texture: '/textures/mars.jpg',
    rotationSpeed: 0.009,
    position: new Vector3(25, -3, -15),
    info: {
      type: 'Terrestrial planet',
      distance: '227.9 million km from Sun',
      temperature: '-63°C',
      day: '24.6 Earth hours'
    }
  },
  {
    name: 'Jupiter',
    diameter: 142984,
    texture: '/textures/jupiter.jpg',
    rotationSpeed: 0.004,
    position: new Vector3(-45, -8, -40),
    info: {
      type: 'Gas giant',
      distance: '778.5 million km from Sun',
      temperature: '-110°C',
      day: '9.9 Earth hours'
    }
  },
  {
    name: 'Saturn',
    diameter: 120536,
    texture: '/textures/saturn.jpg',
    rotationSpeed: 0.0038,
    position: new Vector3(60, 10, -55),
    hasRings: true,
    info: {
      type: 'Gas giant',
      distance: '1.4 billion km from Sun',
      temperature: '-140°C',
      day: '10.7 Earth hours'
    }
  },
  {
    name: 'Uranus',
    diameter: 51118,
    texture: '/textures/uranus.jpg',
    rotationSpeed: 0.003,
    position: new Vector3(-75, -15, -70),
    info: {
      type: 'Ice giant',
      distance: '2.9 billion km from Sun',
      temperature: '-195°C',
      day: '17.2 Earth hours'
    }
  },
  {
    name: 'Neptune',
    diameter: 49528,
    texture: '/textures/neptune.jpg',
    rotationSpeed: 0.0035,
    position: new Vector3(90, 5, -85),
    info: {
      type: 'Ice giant',
      distance: '4.5 billion km from Sun',
      temperature: '-200°C',
      day: '16.1 Earth hours'
    }
  }
];

const PlanetTooltip: React.FC<{ 
  name: string;
  info: {
    type: string;
    distance: string;
    temperature: string;
    day: string;
  }
}> = ({ name, info }) => (
  <Html
    center
    style={{
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      padding: '8px 12px',
      borderRadius: '8px',
      color: '#fff',
      fontSize: '14px',
      pointerEvents: 'none',
      whiteSpace: 'nowrap',
      transform: 'scale(0.75)',
      border: '1px solid rgba(79, 70, 229, 0.4)'
    }}
    distanceFactor={20}
    zIndexRange={[100, 0]}
  >
    <div>
      <div style={{ 
        fontWeight: 'bold', 
        color: '#00F5FF',
        marginBottom: '4px',
        fontSize: '16px'
      }}>
        {name}
      </div>
      <div style={{ color: '#A5B4FC' }}>{info.type}</div>
      <div>{info.distance}</div>
      <div>Temperature: {info.temperature}</div>
      <div>Day length: {info.day}</div>
    </div>
  </Html>
);

const Sun: React.FC = () => {
  const sunRef = useRef<Mesh>(null);
  const objectLimit = useSatelliteStore(state => state.objectLimit);
  const disableAnimations = objectLimit > HIGH_PERFORMANCE_THRESHOLD;
  
  useFrame((state, delta) => {
    if (sunRef.current && !disableAnimations) {
      sunRef.current.rotation.y += 0.002;
    }
  });

  return (
    <group position={new Vector3(-100, 30, -150)}>
      {/* Core sphere */}
      <mesh ref={sunRef}>
        <sphereGeometry args={[15, 32, 32]} />
        <meshBasicMaterial color="#FDB813" />
      </mesh>
      
      {/* Outer glow */}
      <mesh>
        <sphereGeometry args={[16, 32, 32]} />
        <meshBasicMaterial 
          color="#FDB813" 
          transparent 
          opacity={0.3}
        />
      </mesh>

      {/* Corona effect */}
      <mesh>
        <sphereGeometry args={[18, 32, 32]} />
        <meshBasicMaterial 
          color="#FF6B6B" 
          transparent 
          opacity={0.1}
        />
      </mesh>

      {/* Point light for illumination */}
      <pointLight
        color="#FDB813"
        intensity={2}
        distance={1000}
        decay={2}
      />
    </group>
  );
};

const SaturnRings: React.FC<{ scale: number }> = ({ scale }) => {
  return (
    <mesh rotation={[Math.PI / 2, 0, 0]}>
      <ringGeometry args={[scale * 1.2, scale * 2, 64]} />
      <meshStandardMaterial 
        color="#A49B98"
        transparent
        opacity={0.8}
        side={DoubleSide}
      />
    </mesh>
  );
};

const Planet: React.FC<{
  name: string;
  diameter: number;
  texture: string;
  rotationSpeed: number;
  position?: Vector3;
  hasRings?: boolean;
  info: {
    type: string;
    distance: string;
    temperature: string;
    day: string;
  };
}> = ({ name, diameter, texture, rotationSpeed, position, hasRings, info }) => {
  const planetTexture = useTexture(texture);
  const scale = (diameter / 12742) * SCALE_MULTIPLIER;
  const meshRef = useRef<Mesh>(null);
  const [isHovered, setIsHovered] = useState(false);
  const objectLimit = useSatelliteStore(state => state.objectLimit);
  const disableAnimations = objectLimit > HIGH_PERFORMANCE_THRESHOLD;

  useFrame((state, delta) => {
    if (meshRef.current && !disableAnimations) {
      meshRef.current.rotation.y += rotationSpeed * delta;
    }
  });

  return (
    <group 
      position={position}
      onPointerOver={() => setIsHovered(true)}
      onPointerOut={() => setIsHovered(false)}
    >
      <mesh ref={meshRef}>
        <sphereGeometry args={[scale, 32, 32]} />
        <meshStandardMaterial
          map={planetTexture}
          metalness={0.2}
          roughness={0.8}
        />
      </mesh>
      {hasRings && <SaturnRings scale={scale} />}
      {isHovered && <PlanetTooltip name={name} info={info} />}
    </group>
  );
};

const Moon: React.FC = () => {
  const moonRef = useRef<Mesh>(null);
  const orbitRef = useRef<number>(0);
  const [isHovered, setIsHovered] = useState(false);
  const moonTexture = useTexture('/textures/moon.jpg');
  const scale = (MOON.diameter / 12742) * SCALE_MULTIPLIER;
  const objectLimit = useSatelliteStore(state => state.objectLimit);
  const disableAnimations = objectLimit > HIGH_PERFORMANCE_THRESHOLD;

  useFrame((state, delta) => {
    if (moonRef.current && !disableAnimations) {
      // Rotate moon around its axis
      moonRef.current.rotation.y += MOON.rotationSpeed * delta;
      
      // Orbit around Earth
      orbitRef.current += MOON.orbitSpeed * delta;
      const x = Math.cos(orbitRef.current) * MOON.orbitRadius;
      const z = Math.sin(orbitRef.current) * MOON.orbitRadius;
      moonRef.current.position.x = x;
      moonRef.current.position.z = z;
    }
  });

  return (
    <group
      onPointerOver={() => setIsHovered(true)}
      onPointerOut={() => setIsHovered(false)}
    >
      <mesh ref={moonRef} position={[MOON.orbitRadius, 0, 0]}>
        <sphereGeometry args={[scale, 32, 32]} />
        <meshStandardMaterial
          map={moonTexture}
          metalness={0.2}
          roughness={0.8}
        />
      </mesh>
      {isHovered && <PlanetTooltip name={MOON.name} info={MOON.info} />}
    </group>
  );
};

const SolarSystem: React.FC = () => {
  return (
    <group>
      <Sun />
      <Moon />
      {PLANETS.map((planet) => (
        <Planet key={planet.name} {...planet} />
      ))}
    </group>
  );
};

export default SolarSystem; 